import { Slot } from 'radix-ui'
import { type VariantProps, cva } from 'class-variance-authority'
import { Loader2 } from 'lucide-react'
import type * as React from 'react'
import { cn } from '../lib/cn.js'

const buttonVariants = cva(
  // Every target is at least 24px tall including padding (WCAG 2.2 2.5.8);
  // the sm size below is the floor and only for icon buttons in dense tables.
  'inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium transition-colors duration-150 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0 select-none',
  {
    variants: {
      variant: {
        primary:
          'bg-primary text-primary-foreground shadow-xs hover:bg-primary-hover active:bg-primary-active',
        secondary:
          'bg-surface text-foreground border border-border shadow-xs hover:bg-surface-sunken',
        ghost: 'text-foreground-muted hover:bg-surface-sunken hover:text-foreground',
        danger: 'bg-danger text-white shadow-xs hover:brightness-110 active:brightness-95',
        link: 'text-primary underline-offset-4 hover:underline',
      },
      size: {
        sm: 'h-7 px-2.5 text-xs',
        md: 'h-8 px-3',
        lg: 'h-9 px-4 text-md',
        /* Kiosk and POS: sized for a thumb, not a mouse. */
        touch: 'h-14 px-6 text-lg rounded-xl',
        icon: 'size-8 p-0',
        'icon-touch': 'size-14 p-0 rounded-xl',
      },
    },
    defaultVariants: { variant: 'secondary', size: 'md' },
  },
)

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean
  loading?: boolean
}

export function Button({
  className,
  variant,
  size,
  asChild = false,
  loading = false,
  disabled,
  children,
  ...props
}: ButtonProps) {
  const Comp = asChild ? Slot.Root : 'button'

  return (
    <Comp
      className={cn(buttonVariants({ variant, size }), className)}
      disabled={disabled || loading}
      // Screen readers should hear that something is in flight, not just see
      // a spinner appear.
      aria-busy={loading || undefined}
      {...props}
    >
      {loading ? (
        <>
          <Loader2 className="animate-spin" aria-hidden="true" />
          {children}
        </>
      ) : (
        children
      )}
    </Comp>
  )
}

export { buttonVariants }
